import { allowedUnmountedMountingBracketAssociationTypes, MountingBracketAssociationType } from '@libs/associations';
import type { LabeledAttribute, ProductAttribute } from '@libs/attribute.helper';
import type { AssociationProductFragmentEnhanced, ProductFragmentEnhanced } from '@models/enhanced-fragments';
import type {
  DistinctVariant,
  MountingBracketGroup,
} from '@product/VariantSelector/MountingBracketSelector/MountingBracketSelector';

/**
 * Returns only relevant mounting bracket products.
 * Every e-chain product has a lot of associated mounting bracket products, for our e-chain pages
 * we're only interested in a specific subset of mounting brackets which need to be filtered first
 * the results are sorted according to associationSorting
 */
export const getFilteredMountingBrackets = (
  product: ProductFragmentEnhanced,
  associatedProducts: Record<string, AssociationProductFragmentEnhanced>,
  associationSorting?: string,
  evenNumberOfGlieder?: boolean
): MountingBracketGroup[] => {
  // map values of enum into an array
  const mountingBracketAssociationTypes = Object.values(MountingBracketAssociationType).map(String);
  const mountingBracketGroups: MountingBracketGroup[] = [];

  // get all associated mounting brackets
  product.product_to_product_associations
    ?.filter(association => {
      const associatedProduct = associatedProducts[association.productByProductIdTarget?.id || ''];

      const isMounted = (associatedProduct?.attributes?.mounted_bool as LabeledAttribute<boolean>)?.value === true;
      const isUnmountedAllowed =
        association.type &&
        allowedUnmountedMountingBracketAssociationTypes.includes(
          association.type as unknown as MountingBracketAssociationType
        );

      return (
        association.type &&
        mountingBracketAssociationTypes.includes(association.type as string) &&
        (associatedProduct?.attributes?.set_article as LabeledAttribute<boolean>)?.value === true &&
        (isMounted || isUnmountedAllowed)
      );
    })
    ?.forEach(association => {
      // find associationCodes and add mounting bracket groups
      const associatedProduct = associatedProducts[association.productByProductIdTarget?.id || ''];

      let mountingBracketGroup = mountingBracketGroups.find(group => group.key === association.type);
      if (!mountingBracketGroup) {
        // hey eslint, we're already filtering associations where .type is falsy! We don't need to add another check
        // just to make *you* happy (and reduce performance for us).
        mountingBracketGroup = { key: association.type, distinctVariants: [], displayVariant: associatedProduct };
        mountingBracketGroups.push(mountingBracketGroup);
      }

      // add variants for groups
      const associatedProductVariant = (
        associatedProduct?.attributes?.variant_of_mounting_bracket as LabeledAttribute<string>
      )?.value_key;
      const evenOddAttributeName = evenNumberOfGlieder ? 'complete_kit_even_bool' : 'complete_kit_uneven_bool';

      if (
        associatedProductVariant &&
        association.productByProductIdTarget &&
        (associatedProduct?.attributes?.[evenOddAttributeName] as LabeledAttribute<boolean>)?.value === true
      ) {
        const currentVariant: DistinctVariant | undefined = mountingBracketGroup.distinctVariants.find(
          variantProduct => variantProduct.key === associatedProductVariant
        );

        if (!currentVariant) {
          // if the variant does not exist within the group yet, create and push it into mountingVariants
          let mountingVariants: AssociationProductFragmentEnhanced[] | undefined;
          let defaultVariant: AssociationProductFragmentEnhanced | undefined;

          if ((associatedProduct?.attributes?.mounting_variant as LabeledAttribute<unknown>)?.value_key !== undefined) {
            // add the associated product as a mounting variant if mounting is supported
            mountingVariants = [associatedProduct];
          } else {
            defaultVariant = associatedProduct;
          }

          mountingBracketGroup.distinctVariants.push({
            key: associatedProductVariant,
            id: associatedProduct.id,
            id_us: associatedProduct.id_us,
            seriesDescription: associatedProduct?.attributes?.series_description as ProductAttribute<string>,
            assets: associatedProduct?.assets,
            mountingVariants,
            // default variant is needed when mounting is not supported and mountingVariants is empty,
            // otherwise we can't correctly set the selected variant and show properties like the name of the distinctVariant
            defaultVariant,
          });
        } else if (
          (associatedProduct?.attributes?.mounting_variant as LabeledAttribute<unknown>)?.value_key !== undefined
        ) {
          // add the associated product as a mounting variant if mounting is supported
          if (!currentVariant?.mountingVariants) {
            currentVariant.mountingVariants = [];
          }

          currentVariant?.mountingVariants.push(associatedProduct);
        }
      }
    });

  const mountingBracketGroupsWithVariants = mountingBracketGroups?.filter(
    mountingBracketGroup => mountingBracketGroup.distinctVariants?.length
  );

  // sort filtered products according to associationSorting
  if (associationSorting && mountingBracketGroupsWithVariants.length) {
    const sortingOrder: string[] = associationSorting.split(',').map(value => value.trim());
    mountingBracketGroupsWithVariants.sort((a, b) => {
      const posGroupA = sortingOrder.indexOf(`${a.key}`);
      const posGroupB = sortingOrder.indexOf(`${b.key}`);
      return posGroupA - posGroupB;
    });
  }

  return mountingBracketGroupsWithVariants;
};
